import { AbstractControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EventBusService } from '@ups/xplat/core';
import {
  dynamicHasTargetListener,
  dynamicLookupGateEnabled,
} from '../../utils/dynamic-lookup-gate';
import { DYNAMIC_RETRY_LOOKUPS_EVENT } from '../../utils/dynamic-lookup-retry';

/**
 * WI 35277 (code-hr thread 60724, code-tim review): on a lookup-gated form, an
 * `updateOnValueChange` target (e.g. Business Development after a Company pick) must never be
 * left saveable with the PREVIOUS pick's value when its lookup FAILS. Such a target carries
 * `hasFanOutError_dynamic`, which dynamicValidatorNoPrefillError blocks on and the host lists
 * with Retry. A target with no mounted listener (Required Fields Only, a collapsed section) is
 * held while its lookup runs, but an ANSWER it could not hear fails open (STG 2026-10-08: the
 * error could never clear there). Forms without the gate never see these flags.
 */
type FanOutFlagged = AbstractControl & {
  isLoading_dynamic?: boolean;
  hasFanOutError_dynamic?: boolean;
};

/** The control called `name` anywhere under `root` (sections nest groups). */
export function dynamicFindControl(
  root: AbstractControl | null | undefined,
  name: string | undefined
): AbstractControl | undefined {
  if (!root || !name) {
    return undefined;
  }
  const children = (root as { controls?: Record<string, AbstractControl> })
    .controls;
  if (!children || typeof children !== 'object') {
    return undefined;
  }
  if (children[name]) {
    return children[name];
  }
  for (const child of Object.values(children)) {
    const found = dynamicFindControl(child, name);
    if (found) {
      return found;
    }
  }
  return undefined;
}

export function dynamicHasFanOutError(
  control: AbstractControl | null | undefined
): boolean {
  return (
    (control as FanOutFlagged | undefined)?.hasFanOutError_dynamic ?? false
  );
}

/** Set / clear a target's fan-out failure and revalidate (gated forms only). */
export function dynamicSetFanOutError(
  control: AbstractControl | null | undefined,
  value: boolean
): void {
  if (
    control &&
    dynamicLookupGateEnabled(control) &&
    dynamicHasFanOutError(control) !== value
  ) {
    (control as FanOutFlagged).hasFanOutError_dynamic = value;
    control.updateValueAndValidity();
  }
}

/**
 * True when a gated target has no mounted component to hear a push (code-hr thread 60736:
 * judged by the listener count, never by the loading flag, which the target typeahead's own
 * matchTargetProperty search sets again right after a delivered push).
 */
export function dynamicFanOutUnheard(
  control: AbstractControl | null | undefined
): boolean {
  return (
    !!control &&
    dynamicLookupGateEnabled(control) &&
    !dynamicHasTargetListener(control)
  );
}

/** Set an unheard target's loading flag directly and revalidate. */
export function dynamicSetLoadingFlag(
  control: AbstractControl | null | undefined,
  loading: boolean
): void {
  const flagged = control as FanOutFlagged | undefined;
  if (flagged && !!flagged.isLoading_dynamic !== loading) {
    flagged.isLoading_dynamic = loading;
    flagged.updateValueAndValidity();
  }
}

/** Re-run failed fan-outs on the host's Retry event; torn down with `destroy$`. */
export function dynamicObserveFanOutRetry(
  eventBus: EventBusService,
  destroy$: Observable<unknown>,
  retry: () => void
): void {
  eventBus
    .observe(DYNAMIC_RETRY_LOOKUPS_EVENT)
    .pipe(takeUntil(destroy$))
    .subscribe(() => retry());
}
